import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../utils";

export type SegmentedControlOption = {
  value?: string;
  key?: string;
  label: ReactNode;
  id?: string;
  controlsId?: string;
};

type SegmentedControlProps = {
  options: SegmentedControlOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  ariaLabel?: string;
  variant?: "pill" | "underline";
};

const getOptionValue = (option: SegmentedControlOption) =>
  option.value ?? option.key ?? "";

export const SegmentedControl = ({
  options,
  value,
  onChange,
  className,
  ariaLabel,
  variant = "pill",
}: SegmentedControlProps) => {
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    optionIndex: number,
  ) => {
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (optionIndex + 1) % options.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (optionIndex - 1 + options.length) % options.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = options.length - 1;
    }

    if (nextIndex !== null) {
      event.preventDefault();
      onChange(getOptionValue(options[nextIndex]));
      optionRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        variant === "pill"
          ? "inline-flex gap-1 rounded-full bg-surface-secondary p-1"
          : "flex flex-wrap gap-2 border-b border-border-subtle",
        className,
      )}
    >
      {options.map((option, optionIndex) => {
        const optionValue = getOptionValue(option);
        const isActive = optionValue === value;

        return (
          <button
            key={optionValue || String(option.label)}
            ref={(node) => {
              optionRefs.current[optionIndex] = node;
            }}
            id={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={option.controlsId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(optionValue)}
            onKeyDown={(event) => handleKeyDown(event, optionIndex)}
            className={cn(
              "px-4 text-body font-medium transition-colors duration-fast ease-standard focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface-secondary",
              variant === "pill" ? "rounded-pill py-1" : "border-b-2 py-3",
              variant === "pill"
                ? isActive
                  ? "relative z-10 bg-surface-primary text-content-primary shadow-surface"
                  : "text-content-muted hover:bg-surface-tertiary/60 hover:text-content-secondary"
                : isActive
                  ? "border-border-accent text-content-accent"
                  : "border-transparent text-content-secondary hover:text-content-primary",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
